import { Skeleton, SkeletonCard, SkeletonText } from '../SkeletonComponents';

/**
 * Skeleton member row matching MemberListItem component structure exactly
 * Uses same padding and layout as: py-3 sm:py-4, px-6 for row content
 */
function SkeletonMemberRow({ showActions = true }: { showActions?: boolean }) {
  return (
    <div
      className="flex items-center justify-between py-3 sm:py-4 px-6"
      data-testid="skeleton-member-row"
    >
      {/* Left: Avatar and member info - matches MemberInfo component */}
      <div className="flex items-center min-w-0">
        {/* Avatar - md size = w-10 h-10 with mr-3 */}
        <Skeleton className="w-10 h-10 rounded-full shrink-0 mr-3" />
        <div className="min-w-0 flex-1">
          {/* Name row with badge */}
          <div className="flex items-center flex-wrap gap-1">
            <SkeletonText className="w-24 sm:w-32" />
            {/* Owner/Admin badge - only show for first item */}
            <Skeleton className="w-16 h-5 rounded-sm hidden sm:block" />
          </div>
          {/* Join date row with calendar icon area */}
          <div className="flex items-center mt-1">
            <Skeleton className="w-3 h-3 rounded-sm mr-1 shrink-0" />
            <SkeletonText className="w-28 sm:w-36" size="sm" />
          </div>
        </div>
      </div>
      {/* Right: Action buttons - matches rightContent area */}
      {showActions && (
        <div className="ml-2 sm:ml-4 shrink-0">
          <div className="flex items-center gap-1">
            <Skeleton className="w-8 h-8 rounded-md" />
            <Skeleton className="w-8 h-8 rounded-md" />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Skeleton entry row - matches MemberActionRow (icon disc + label)
 */
function SkeletonActionRow() {
  return (
    <div
      className="flex items-center py-3 sm:py-4 px-6"
      data-testid="skeleton-action-row"
    >
      <Skeleton className="w-10 h-10 rounded-full shrink-0 mr-3" />
      <SkeletonText className="w-40 sm:w-52" />
    </div>
  );
}

/**
 * Skeleton for the Members tab CONTENT ONLY
 * Used within GroupPageLayout - no header or tabs included
 * Matches: GroupMembersPage roster card
 */
export function MembersContentSkeleton() {
  return (
    <div data-testid="members-skeleton">
      <SkeletonCard
        className="overflow-hidden p-0"
        data-testid="skeleton-members-card"
      >
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          <SkeletonActionRow />
          <SkeletonActionRow />

          <div className="px-6 py-2">
            <SkeletonText className="w-24" size="sm" />
          </div>

          {[1, 2, 3, 4, 5].map((i, index) => (
            <div key={i} data-testid="skeleton-member-item">
              {/* First 3 members show action buttons, last 2 don't (simulating non-admin view) */}
              <SkeletonMemberRow showActions={index < 3} />
            </div>
          ))}
        </div>
      </SkeletonCard>
    </div>
  );
}

/**
 * Full page skeleton for the Members page (standalone use)
 * @deprecated Use MembersContentSkeleton with GroupPageLayout instead
 */
export function MembersPageSkeleton() {
  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <MembersContentSkeleton />
    </div>
  );
}
